import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SYSTEMS_PER_GALAXY, SYSTEM_POSITION_MAX, type SolarSystemPlanet, type SolarSystemView, type SunStatus } from '@kaeliux/shared';
import { getDriftingSystemAsteroids, getSystemStarImageIndex, type SystemAsteroid } from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { useUniverseStore } from '../store/universeStore';
import { useNow } from '../hooks/useNow';
import { useSystemTransition } from '../hooks/useSystemTransition';
import { getSolarSystem } from '../services/galaxy.service';
import { useMyAlliance } from '../hooks/useMyAlliance';
import { useTeamPlanet } from '../hooks/useTeamPlanet';
import { Starfield } from '../components/Starfield';
import { Button } from '../components/ui/Button';
import { IconButton } from '../components/ui/IconButton';
import { Panel } from '../components/ui/Panel';
import { Icon } from '../components/ui/Icon';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import SunDetails from '../components/SunDetails';
import { GALAXY_MARKERS, GALAXY_LEGEND_ENTRIES, type GalaxyMarkerType } from './galaxyMarkers';
import asteroid1 from '../assets/images/galaxy/asteroid1.png';
import asteroid2 from '../assets/images/galaxy/asteroid2.png';
import asteroid3 from '../assets/images/galaxy/asteroid3.png';
import asteroid4 from '../assets/images/galaxy/asteroid4.png';
import asteroid5 from '../assets/images/galaxy/asteroid5.png';
import asteroid6 from '../assets/images/galaxy/asteroid6.png';
import star1 from '../assets/images/galaxy/star1.png';
import star2 from '../assets/images/galaxy/star2.png';
import star3 from '../assets/images/galaxy/star3.png';
import star4 from '../assets/images/galaxy/star4.png';
import star5 from '../assets/images/galaxy/star5.png';
import star6 from '../assets/images/galaxy/star6.png';
import star7 from '../assets/images/galaxy/star7.png';
import star8 from '../assets/images/galaxy/star8.png';

const ASTEROID_IMAGES = [asteroid1, asteroid2, asteroid3, asteroid4, asteroid5, asteroid6];
const STAR_IMAGES = [star1, star2, star3, star4, star5, star6, star7, star8];
const LOADING_STARFIELD_SPEED = 4;
const IDLE_STARFIELD_SPEED = 0.1;

/** SystemAsteroid.imageIndex (1-based) → its lifetime art variant. */
function asteroidImage(asteroid: SystemAsteroid): string {
    return ASTEROID_IMAGES[asteroid.imageIndex - 1] ?? asteroid1;
}

/** getSystemStarImageIndex (1-based) → this system's star art variant. */
function starImage(galaxy: number, system: number): string {
    return STAR_IMAGES[getSystemStarImageIndex(galaxy, system) - 1] ?? star1;
}

/** SolarSystemPlanet.planetType (1-based) → its preset art variant. */
function planetImage(planetType: number): string {
    return new URL(`../assets/images/planets/planet_${planetType}.png`, import.meta.url).href;
}

const POSITIONS_PER_RING = 8;

/**
 * 24 positions around the sun as three concentric rings of 8 (positions
 * 1–8 inner, 9–16 middle, 17–24 outer). The middle ring is rotated half a
 * step so tiles never stack radially. Percentages of the map box — the box
 * is square, so rings render as circles.
 */
const RINGS = [
    { radius: 21, offsetDeg: 0 },
    { radius: 32.5, offsetDeg: 22.5 },
    { radius: 42, offsetDeg: 0 },
] as const;

function positionPercent(position: number): { x: number; y: number } {
    const ringIndex = Math.min(Math.floor((position - 1) / POSITIONS_PER_RING), RINGS.length - 1);
    const ring = RINGS[ringIndex] ?? RINGS[0];
    const step = ((position - 1) % POSITIONS_PER_RING) / POSITIONS_PER_RING;
    const angle = step * 2 * Math.PI - Math.PI / 2 + (ring.offsetDeg * Math.PI) / 180;
    return { x: 50 + ring.radius * Math.cos(angle), y: 50 + ring.radius * Math.sin(angle) };
}

/**
 * Corner classes for the details popup: diagonally opposite the clicked
 * position's quadrant so the popup never covers what was clicked.
 */
function popupCornerClass(position: number): string {
    const { x, y } = positionPercent(position);
    const horizontal = x < 50 ? 'right-3' : 'left-3';
    const vertical = y < 50 ? 'bottom-3' : 'top-3';
    return `${horizontal} ${vertical}`;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/** Wrapping previous/next over 1..max. */
function step(value: number, delta: number, max: number): number {
    return ((value - 1 + delta + max) % max) + 1;
}

interface PositionTileProps {
    position: number;
    planet: SolarSystemPlanet | undefined;
    asteroid: SystemAsteroid | undefined;
    isOwn: boolean;
    isSelected: boolean;
    onSelect: (position: number) => void;
}

/** Resolves a tile to its marker type so the marker map owns the colour. */
function markerTypeForTile(
    planet: SolarSystemPlanet | undefined,
    asteroid: SystemAsteroid | undefined,
    isOwn: boolean,
): GalaxyMarkerType {
    if (planet) {
        if (planet.isTeamPlanet) return 'teamPlanet';
        if (isOwn) return 'ownPlanet';
        return 'other';
    }
    if (asteroid) return 'asteroid';
    return 'empty';
}

function PositionTile({ position, planet, asteroid, isOwn, isSelected, onSelect }: PositionTileProps) {
    const { x, y } = positionPercent(position);
    // Mobile renders the whole system compact — every planet/lot small —
    // with the sm: sizes restoring the full desktop art.
    const planetSize = 'w-12 h-12 sm:w-24 sm:h-24';
    const badgeSize = 'w-4 h-4 sm:w-8 sm:h-8';
    const lotSize = 'w-8 h-8 sm:w-16 sm:h-16';
    const marker = GALAXY_MARKERS[markerTypeForTile(planet, asteroid, isOwn)];
    const label = [
        planet
            ? `Position ${position} — ${planet.name}, owned by ${planet.ownerName}${isOwn ? ' (your planet)' : ''}${planet.isTeamPlanet ? ` — Team planet${planet.allianceTag ? ` of [${planet.allianceTag}]` : ''}` : ''}`
            : `Position ${position} — empty`,
        asteroid ? '— asteroid in transit' : '',
    ].filter(Boolean).join(' ');
    return (
        <button
            type="button"
            data-testid={`galaxy-position-${position}`}
            aria-label={label}
            title={label}
            aria-pressed={isSelected}
            onClick={() => onSelect(position)}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center w-12 sm:w-24 focus:outline-none group"
            style={{ left: `${x}%`, top: `${y}%` }}
        >
            {planet ? (
                <>
                    <span className="relative">
                        <span
                            className={`block ${planetSize} rounded-full overflow-hidden transition-shadow ${marker.borderStyle} ${marker.border} ${marker.glow ?? ''} ${isSelected ? 'ring-2 ring-status-warning' : 'group-hover:ring-2 group-hover:ring-content-muted'}`}
                        >
                            <img src={planetImage(planet.planetType)} alt="" className="w-full h-full object-cover" />
                        </span>
                        {asteroid && (
                            <img
                                src={asteroidImage(asteroid)}
                                alt=""
                                className={`absolute -right-2 -top-2 ${badgeSize} rounded-full ${GALAXY_MARKERS.asteroid.border} rotate-12`}
                            />
                        )}
                        {planet.isTeamPlanet && (
                            <span
                                data-testid="team-planet-badge"
                                className={`absolute -top-1 -left-1 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-surface-void ${GALAXY_MARKERS.teamPlanet.badge}`}
                            />
                        )}
                    </span>
                    <span
                        className={`mt-0.5 max-w-full truncate text-micro leading-tight ${marker.text}`}
                    >
                        {planet.isTeamPlanet ? `[${planet.allianceTag ?? 'Team'}]` : isOwn ? 'You' : planet.ownerName}
                    </span>
                </>
            ) : asteroid ? (
                <img
                    src={asteroidImage(asteroid)}
                    alt=""
                    className={`${lotSize} rounded-full transition-colors -rotate-12 ${isSelected ? 'border-status-warning ring-2 ring-status-warning' : `${GALAXY_MARKERS.asteroid.border} group-hover:ring-2 group-hover:ring-content-muted`
                        }`}
                />
            ) : (
                <span
                    className={`${lotSize} rounded-full ${GALAXY_MARKERS.empty.borderStyle} transition-colors ${isSelected
                        ? 'border-status-warning bg-status-warning/10'
                        : `bg-surface-void/40 group-hover:border-content-muted`
                        }`}
                />
            )}
        </button>
    );
}

interface DetailsPopupProps {
    galaxy: number;
    system: number;
    position: number;
    planet: SolarSystemPlanet | undefined;
    asteroid: SystemAsteroid | undefined;
    now: number;
    isOwn: boolean;
    /** Only meaningful when `position === 0` (the Sun). */
    sun: SunStatus | undefined;
    viewerUserId: string | undefined;
    /** Only the leader of an alliance with a converted Team planet may Sun Attack (issue #158). */
    canSunAttack: boolean;
    /** `useMyAlliance`/`useTeamPlanet` still loading — show a pending affordance instead of nothing. */
    canSunAttackPending: boolean;
    onClose: () => void;
    onGoToPlanet: (planetId: string) => void;
}

function AsteroidNote({ asteroid, now }: { asteroid: SystemAsteroid; now: number }) {
    const minutesToMove = Math.max(1, Math.ceil((asteroid.nextMoveAt - now) / 60_000));
    return (
        <p className="text-status-warning text-label flex items-center gap-1.5">
            <Icon name="asteroid" size={14} aria-hidden="true" />
            Asteroid in transit — drifts outward in ~{minutesToMove} min
        </p>
    );
}

/**
 * Non-modal details popup, floated inside the map at the corner opposite
 * the clicked position. No backdrop — the rest of the map stays clickable.
 */
function DetailsPopup({ galaxy, system, position, planet, asteroid, now, isOwn, sun, viewerUserId, canSunAttack, canSunAttackPending, onClose, onGoToPlanet }: DetailsPopupProps) {
    const coords = `${galaxy}:${system}:${position}`;
    const planetMarker = planet
        ? GALAXY_MARKERS[planet.isTeamPlanet ? 'teamPlanet' : isOwn ? 'ownPlanet' : 'other']
        : undefined;

    return (
        <div
            data-testid="galaxy-info-panel"
            role="status"
            className={`absolute z-10 w-72 max-w-[calc(100%-1.5rem)] rounded-panel border border-hairline bg-surface-panel backdrop-blur-panel p-4 shadow-e4 ${popupCornerClass(position)}`}
        >
            <IconButton
                aria-label="Close details"
                onClick={onClose}
                className="absolute right-2 top-2"
            >
                <Icon name="close" size={16} aria-hidden="true" />
            </IconButton>
            {position === 0 ? (
                <SunDetails galaxy={galaxy} system={system} sun={sun} viewerUserId={viewerUserId} canSunAttack={canSunAttack} canSunAttackPending={canSunAttackPending} now={now} />
            ) : planet ? (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 pr-6">
                        <span className={`w-12 h-12 shrink-0 rounded-full overflow-hidden border-2 ${planetMarker?.border}`}>
                            <img src={planetImage(planet.planetType)} alt={planet.name} className="w-full h-full object-cover" />
                        </span>
                        <div className="min-w-0">
                            <h3 className="text-title font-semibold text-content-primary truncate">{planet.name}</h3>
                            <p className="text-content-muted text-label truncate">
                                {isOwn ? 'Your planet' : `Owned by ${planet.ownerName}`}
                                {planet.ownerRace ? ` · ${planet.ownerRace}` : ''}
                            </p>
                        </div>
                    </div>
                    {planet.isTeamPlanet && (
                        <p className={`${GALAXY_MARKERS.teamPlanet.text} text-label font-semibold`} data-testid="team-planet-marker">
                            Team planet{planet.allianceTag ? ` of [${planet.allianceTag}]` : ''}
                        </p>
                    )}
                    <p className="text-content-muted text-label">Coordinates <span className="font-mono text-content-secondary">{coords}</span></p>
                    {asteroid && <AsteroidNote asteroid={asteroid} now={now} />}
                    {isOwn ? (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onGoToPlanet(planet.planetId)}
                        >
                            Go to Planet
                        </Button>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {([['Spy', 'espionage'], ['Attack', 'attack'], ['Pirate', 'pirate'], ['Recycle', 'recycle']] as const).map(([label, mission]) => (
                                <Link
                                    key={label}
                                    to={`/fleets?mission=${mission}&target=${coords}`}
                                    className={linkButtonClasses('secondary', 'sm')}
                                >
                                    {label}
                                </Link>
                            ))}
                        </div>
                    )}
                    {asteroid && (
                        <Link to={`/fleets?mission=harvestAsteroid&target=${coords}`} className={linkButtonClasses('secondary', 'sm')}>
                            Mine Asteroid
                        </Link>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-2 pr-6">
                    <h3 className="text-title font-semibold text-content-primary">Unoccupied position</h3>
                    <p className="text-content-muted text-label">Coordinates <span className="font-mono text-content-secondary">{coords}</span></p>
                    {asteroid && <AsteroidNote asteroid={asteroid} now={now} />}
                    <p className="text-content-muted text-label">No planet has been founded here yet.</p>
                    <Link to={`/fleets?mission=colonize&target=${coords}`} className={linkButtonClasses('primary', 'sm')}>
                        Colonize
                    </Link>
                    {asteroid && (
                        <Link to={`/fleets?mission=harvestAsteroid&target=${coords}`} className={linkButtonClasses('secondary', 'sm')}>
                            Mine Asteroid
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}

export default function GalaxyPage() {
    const params = useParams<{ galaxy?: string; system?: string }>();
    const navigate = useNavigate();
    const { profile } = useAuthStore();
    const currentPlanet = usePlanetStore((s) => s.currentPlanet);
    const setActivePlanet = usePlanetStore((s) => s.setActivePlanet);
    const { sunAges } = useUniverseStore();
    const [selected, setSelected] = useState<number | null>(null);
    const [legendDismissed, setLegendDismissed] = useState(false);

    // Sun Attack is Team-planet-only, leader-dispatched (issue #158) — the
    // quick-dispatch link only ever shows for a leader whose alliance has a
    // converted Team planet to send it from. Both queries are gated behind
    // the Sun popup actually being open (`selected === 0`): `getAlliance` is
    // a Cloud Function POST, not a free read, and `getTeamPlanet` opens a
    // server-side WRITE transaction (it replays and re-commits the Team
    // planet) — paying either on every Galaxy-map mount, for every player
    // (the common case has no alliance at all), would tax the single
    // highest-traffic page in the app for a link that's shown inside a
    // popup most visits never open. Cached under the same query keys
    // `AllianceTeamPlanetTab`/`TeamPlanetConversionCard` use, so opening the
    // Sun popup after visiting either page still doesn't re-fetch.
    const sunPopupOpen = selected === 0;
    const allianceQuery = useMyAlliance({ enabled: sunPopupOpen });
    const isLeader = allianceQuery.data?.viewerRole === 'leader';
    const teamPlanetQuery = useTeamPlanet({ enabled: sunPopupOpen && isLeader });
    const canSunAttack = isLeader && !!teamPlanetQuery.data;
    // While these load, `canSunAttack` reads false like "not a leader" — show
    // a pending affordance instead of letting the link silently pop in a
    // beat after the popup opens.
    const canSunAttackPending = sunPopupOpen && (allianceQuery.isPending || (isLeader && teamPlanetQuery.isPending));

    // Switch which planet the app is viewing (same mechanism PlanetSwitcher
    // uses) rather than a plain `/planet` link, which always lands on
    // whichever planet was already active — not necessarily the one just
    // clicked on the map.
    function goToPlanet(planetId: string) {
        if (profile?.id) setActivePlanet(planetId, profile.id).catch(console.error);
        navigate('/planet');
    }

    // Deployment-driven galaxy count: one sunAges entry per galaxy.
    const galaxyCount = Math.max(1, Object.keys(sunAges ?? {}).length);

    // URL coordinates win; otherwise start at the player's own system once
    // the planet is known.
    const coords = useMemo(() => {
        const galaxy = parseInt(params.galaxy ?? '', 10);
        const system = parseInt(params.system ?? '', 10);
        if (Number.isFinite(galaxy) && Number.isFinite(system)) {
            return {
                galaxy: clamp(galaxy, 1, Math.max(galaxyCount, 1)),
                system: clamp(system, 1, SYSTEMS_PER_GALAXY),
            };
        }
        if (currentPlanet) {
            return {
                galaxy: currentPlanet.coordinates.galaxy,
                system: currentPlanet.coordinates.system,
            };
        }
        return null;
    }, [params.galaxy, params.system, currentPlanet, galaxyCount]);

    const { data: view, isError, error, refetch } = useQuery({
        queryKey: ['solarSystem', coords?.galaxy, coords?.system],
        queryFn: () => getSolarSystem(coords!.galaxy, coords!.system),
        enabled: coords !== null,
        staleTime: 30_000,
    });

    // Switching systems fades the old one out while ramping the background
    // up, then — once both that transition-out and the new system's data
    // are ready — swaps in the new system and fades it in while ramping the
    // background back down. See useSystemTransition for the phase timing.
    const targetKey = coords ? `${coords.galaxy}:${coords.system}` : null;
    const { phase, displayedView } = useSystemTransition<SolarSystemView>(
        targetKey,
        view,
        (v) => `${v.galaxy}:${v.system}`,
    );

    // The starfield ramp and the map's opacity fade both key off the same
    // phase, so they always start together: sped up / hidden for the whole
    // 'transitioning' window, idle / visible otherwise.
    const isLoadingVisual = phase === 'transitioning';
    const isContentVisible = phase === 'shown' || phase === 'showing';

    // Asteroids drift one position every 45–60 min; a 30 s refresh keeps
    // their movement visible without a page reload. Keyed off the
    // *displayed* system (not the target being navigated to) so they stay
    // in sync with whatever's actually on screen during a fade.
    const now = useNow(30_000);
    const asteroidsByPosition = useMemo(() => {
        const map = new Map<number, SystemAsteroid>();
        if (!displayedView) return map;
        for (const asteroid of getDriftingSystemAsteroids(displayedView.galaxy, displayedView.system, new Date(now))) {
            // Two asteroids can briefly share a position; show the first.
            if (!map.has(asteroid.position)) map.set(asteroid.position, asteroid);
        }
        return map;
    }, [displayedView, now]);

    if (coords === null) {
        return (
            <main className="max-w-7xl mx-auto p-4">
                <div className="text-content-muted">Locating your home system…</div>
            </main>
        );
    }

    const { galaxy, system } = coords;
    const planetsByPosition = new Map<number, SolarSystemPlanet>(
        (displayedView?.planets ?? []).map((planet) => [planet.position, planet]),
    );
    const selectedPlanet = selected !== null ? planetsByPosition.get(selected) : undefined;
    const isOwnPlanet = (planet: SolarSystemPlanet | undefined) =>
        !!planet && !!profile && planet.ownerId === profile.id;

    const goTo = (nextGalaxy: number, nextSystem: number) => {
        setSelected(null);
        navigate(`/galaxy/${nextGalaxy}/${nextSystem}`);
    };

    return (
        <main className="max-w-7xl mx-auto p-2">
            {/* Full-viewport animated background — ramps up while a system
                loads/switches, settles once it's shown (no separate
                "loading…" message needed). z-index matches SpaceBackdrop's
                (issue #182) — both are `position: fixed` with no stacking
                context in between, so equal z-index falls back to DOM order:
                AppShell mounts SpaceBackdrop before <Outlet>, so this div,
                rendered later as this page's own content, paints on top of
                it instead of being hidden behind it. This must NOT be paired
                with `isolate` — that would scope the stacking context to
                this <main> and make its fixed (viewport-spanning) content
                paint in front of the top nav bar, which lives outside <main>
                as an earlier sibling. */}
            <div className="fixed inset-0 z-[-1]">
                <Starfield speed={isLoadingVisual ? LOADING_STARFIELD_SPEED : IDLE_STARFIELD_SPEED} />
            </div>
            <div className="flex flex-col">
                <div className="bg-surface-raised border border-hairline rounded-panel h-16 flex-1 flex items-start justify-between px-4 sm:gap-y-2 gap-y-2">
                    <div>
                        <h2 className="text-heading font-bold text-content-primary">Galaxy Map</h2>
                        <span className="text-content-muted">
                            {galaxy} : {system}
                        </span>
                    </div>
                    {/* Navigation between galaxies and solar systems */}
                    <div className="flex flex-col items-end gap-2 text-body">
                        <div className="flex items-center gap-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                aria-label="Previous galaxy"
                                disabled={galaxyCount <= 1}
                                onClick={() => goTo(step(galaxy, -1, galaxyCount), system)}
                            >
                                ‹
                            </Button>
                            <span className="text-content-secondary text-center">Galaxy {galaxy}/{galaxyCount}</span>
                            <Button
                                variant="secondary"
                                size="sm"
                                aria-label="Next galaxy"
                                disabled={galaxyCount <= 1}
                                onClick={() => goTo(step(galaxy, 1, galaxyCount), system)}
                            >
                                ›
                            </Button>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                aria-label="Previous system"
                                onClick={() => goTo(galaxy, step(system, -1, SYSTEMS_PER_GALAXY))}
                            >
                                ‹
                            </Button>
                            <span className="text-content-secondary text-center">System {system}/{SYSTEMS_PER_GALAXY}</span>
                            <Button
                                variant="secondary"
                                size="sm"
                                aria-label="Next system"
                                onClick={() => goTo(galaxy, step(system, 1, SYSTEMS_PER_GALAXY))}
                            >
                                ›
                            </Button>
                        </div>
                    </div>
                </div>
                <div
                    data-testid="solar-system-map"
                    className="relative flex-1 aspect-[3/4] sm:aspect-square w-full sm:max-w-[calc(100vh_-_18rem)] min-w-[18rem] mx-auto rounded-panel border-hairline overflow-hidden">
                    {isError ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                            <p className="text-status-danger text-body px-6 text-center">
                                {error instanceof Error ? error.message : 'Failed to scan this system'}
                            </p>
                            <Button variant="secondary" size="sm" onClick={() => { refetch().catch(() => { }); }}>
                                Retry
                            </Button>
                        </div>
                    ) : (
                        // Always mounted (never conditionally unrendered) so the
                        // opacity transition actually animates instead of
                        // snapping — it fades out the last-loaded system while a
                        // new one is (still) loading, and fades the new one back
                        // in once it settles. Star/tiles/popup key off
                        // `displayedView`'s OWN coordinates, not the live
                        // navigation target, so the fade-out still shows the
                        // system that's actually fading away.
                        <div
                            className={`absolute inset-0 transition-opacity duration-200 ease-in-out ${isContentVisible && displayedView ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                }`}
                        >
                            {displayedView && (
                                <>
                                    {/* Orbit guides — ellipses in the widescreen box */}
                                    {RINGS.map((ring) => (
                                        <span
                                            key={ring.radius}
                                            aria-hidden="true"
                                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-hairline pointer-events-none"
                                            style={{ width: `${ring.radius * 2}%`, height: `${ring.radius * 2}%` }}
                                        />
                                    ))}
                                    <button
                                        type="button"
                                        data-testid="galaxy-sun"
                                        aria-label={`Sun of system ${displayedView.galaxy}:${displayedView.system}${displayedView.sun?.underAttack ? ' — under attack' : ''}${displayedView.sun?.control ? ` — held${displayedView.sun.control.alliance ? ` by [${displayedView.sun.control.alliance.tag}]` : ''}` : ''}`}
                                        aria-pressed={selected === 0}
                                        onClick={() => setSelected(0)}
                                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 focus:outline-none group"
                                    >
                                        <img
                                            src={starImage(displayedView.galaxy, displayedView.system)}
                                            alt={`Star of system ${displayedView.galaxy}:${displayedView.system}`}
                                            title={`Star — ${displayedView.galaxy}:${displayedView.system}:0`}
                                            className={`w-14 h-14 sm:w-32 sm:h-32 rounded-full transition-shadow ${selected === 0 ? 'ring-2 ring-status-warning' : 'group-hover:ring-2 group-hover:ring-content-muted'}`}
                                        />
                                        {displayedView.sun && (displayedView.sun.underAttack || displayedView.sun.onCooldown) && (
                                            <span
                                                data-testid="sun-status-badge"
                                                className={`absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-surface-void ${displayedView.sun.underAttack ? 'bg-status-danger' : 'bg-status-warning'}`}
                                            />
                                        )}
                                        {displayedView.sun?.control && (
                                            <span
                                                data-testid="sun-control-badge"
                                                className={`absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-badge border px-1.5 text-micro font-semibold ${displayedView.sun.control.viewerIsController ? 'border-status-success/60 bg-status-success/20 text-content-primary' : 'border-hairline bg-surface-panel text-content-primary'}`}
                                            >
                                                {displayedView.sun.control.alliance ? `[${displayedView.sun.control.alliance.tag}]` : 'Held'}
                                            </span>
                                        )}
                                    </button>
                                    {Array.from({ length: SYSTEM_POSITION_MAX }, (_, i) => i + 1).map((position) => {
                                        const planet = planetsByPosition.get(position);
                                        return (
                                            <PositionTile
                                                key={position}
                                                position={position}
                                                planet={planet}
                                                asteroid={asteroidsByPosition.get(position)}
                                                isOwn={isOwnPlanet(planet)}
                                                isSelected={selected === position}
                                                onSelect={setSelected}
                                            />
                                        );
                                    })}
                                    {selected !== null && (
                                        <DetailsPopup
                                            galaxy={displayedView.galaxy}
                                            system={displayedView.system}
                                            position={selected}
                                            planet={selectedPlanet}
                                            asteroid={asteroidsByPosition.get(selected)}
                                            now={now}
                                            isOwn={isOwnPlanet(selectedPlanet)}
                                            sun={displayedView.sun}
                                            viewerUserId={profile?.id}
                                            canSunAttack={canSunAttack}
                                            canSunAttackPending={canSunAttackPending}
                                            onClose={() => setSelected(null)}
                                            onGoToPlanet={goToPlanet}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
                {!legendDismissed &&
                    <Panel
                        variant="solid"
                        padding="sm"
                        className="absolute bottom-28"
                        data-testid="galaxy-legend-panel"
                    >
                        <IconButton
                            aria-label="Close legend"
                            onClick={() => setLegendDismissed(true)}
                            className="absolute -right-2 -top-2"
                            size="sm"
                        >
                            <Icon name="close" size={12} aria-hidden="true" />
                        </IconButton>
                        {GALAXY_LEGEND_ENTRIES.map(({ type }) => {
                            const marker = GALAXY_MARKERS[type];
                            return (
                                <div key={type} className="flex items-center gap-2">
                                    {type === 'asteroid' ? (
                                        <img src={asteroid1} alt="" className={`w-4 h-4 rounded-full ${marker.border}`} />
                                    ) : (
                                        <span className={`w-4 h-4 rounded-full ${marker.borderStyle} ${marker.border}`} />
                                    )}
                                    <span className="text-content-secondary text-body">{marker.label}</span>
                                </div>
                            );
                        })}
                    </Panel>
                }
            </div>
        </main>
    );
}
